import { Timestamp } from "@bufbuild/protobuf";

import React from "react";

import { Box, Chip, LinearProgress } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";

import { Gateway } from "@safetyculture/s12-apis-connect-web/s12/sensors/v1/gateway_pb";

import { convertTimestampToDate } from "../../helpers/time";

function CustomExportToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport
        // Removes printOptions as default toolbar option
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  );
}

function displayVendor(str: string) {
  switch (str) {
    case "mydevices":
      return "MyDevices";
    case "monnit":
      return "Monnit";
    case "definium":
      return "Definium";
    case "s12test":
      return "s12test";
    default:
      return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

function CustomNoRowsOverlay() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Box sx={{ mt: 1 }}>No results found.</Box>
    </Box>
  );
}
export function getConnectionType(id: string, sourceName: string): string {
  const lowerSourceName = sourceName.toLowerCase();
  if (id.startsWith("eui-647")) {
    return "Cellular";
  }
  if (id.startsWith("eui-0000")) {
    return "Ethernet";
  }
  if (lowerSourceName === "monnit" || lowerSourceName === "definium") {
    return sourceName.charAt(0).toUpperCase() + sourceName.slice(1);
  }
  return lowerSourceName;
}

function getDeviceId(
  displayId: string,
  sourceName: string,
  sourceId: string
): string {
  if (sourceName.toLowerCase() == "definium") {
    return "";
  } else if (sourceName.toLowerCase() == "monnit") {
    return sourceId;
  } else {
    return displayId;
  }
}

export function isGatewayConnected(lastSeenTime: Timestamp): boolean {
  const currentTimeUnix = Math.floor(Date.now() / 1000);
  const lastSeenTimeUnix = Number(lastSeenTime.seconds);

  // Check if the lastSeenTime is within the last hour (3600 seconds)
  return lastSeenTimeUnix ? currentTimeUnix - lastSeenTimeUnix < 3600 : false;
}

const OrganisationGatewaysTable = ({
  gatewaysList,
  isLoading,
}: {
  gatewaysList: Gateway[];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [pageSize, setPageSize] = React.useState<number>(100);
  const [page, setPage] = React.useState<number>(0);
  // 100 rows is the maximum number of rows that can be rendered for MIT version of Data Grid

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Device Name",
      width: 180,
      renderCell({ row }) {
        return <Box textAlign="left">{row.name}</Box>;
      },
    },
    {
      field: "lastSeenTime",
      headerName: "Status",
      width: 150,
      flex: 1,
      align: "left",
      valueFormatter({ value }) {
        return isGatewayConnected(value) ? "Connected" : "Disconnected";
      },
      renderCell({ row }) {
        const isConnected = isGatewayConnected(row.lastSeenTime);

        return (
          <Box>
            {isConnected ? (
              <Chip label="Connected" color="success" />
            ) : (
              <Chip label="Disconnected" color="error" />
            )}
          </Box>
        );
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: 110,
      flex: 1,
      valueGetter: ({ row }) => row.locationName,
      renderCell({ row }) {
        return <Box textAlign="left">{row.locationName}</Box>;
      },
    },
    {
      field: "type",
      headerName: "Type",
      width: 110,
      flex: 1,
      valueGetter: ({ row }) =>
        getConnectionType(row.displayId, row.sourceName),
      renderCell({ row }) {
        return (
          <Box textAlign="left">
            {getConnectionType(row.displayId, row.sourceName)}
          </Box>
        );
      },
    },
    {
      field: "device id",
      headerName: "Device ID",
      width: 360,
      valueGetter: ({ row }) => row.displayId,
      renderCell({ row }) {
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            textAlign="left"
          >
            <Box>
              {getDeviceId(row.displayId, row.sourceName, row.sourceId)}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "last seen",
      headerName: "Last Seen",
      width: 160,
      flex: 1,
      // Disabled sorting on this because multiple reading times could appear
      sortable: false,
      align: "left",
      valueGetter({ row }) {
        return row.lastSeenTime
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.lastSeenTime)
            ).toRelative()
          : "";
      },
      renderCell({ row }) {
        const nullTime = new Timestamp({
          seconds: BigInt(0),
          nanos: 0,
        });
        return (
          <Box>
            {row.lastSeenTime.seconds !== nullTime.seconds
              ? DateTime.fromJSDate(
                  convertTimestampToDate(row.lastSeenTime)
                ).toRelative()
              : ""}
          </Box>
        );
      },
    },
    {
      field: "no of sensors",
      headerName: "# of Sensors",
      width: 110,
      flex: 1,
      align: "left",
      valueGetter: ({ row }) => {
        return row.connectedSensors.length;
      },
      renderCell: ({ row }) => {
        return <Box>{row.connectedSensors.length}</Box>;
      },
    },
    {
      field: "vendor",
      headerName: "vendor",
      width: 110,
      flex: 1,
      align: "left",
      valueGetter: ({ row }) => {
        return row.sourceName;
      },
      renderCell: ({ row }) => {
        return <Box>{displayVendor(row.sourceName)}</Box>;
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: 700, width: "100%" }}>
        <DataGrid
          rows={gatewaysList}
          columns={columns}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.sourceId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : CustomNoRowsOverlay,
            Toolbar: CustomExportToolbar,
          }}
          data-testid="org-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            sorting: {
              sortModel: [{ field: "location", sort: "asc" }],
            },
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          density="compact"
          sx={{
            // Removes the default cell border on click
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Box>
    </>
  );
};

export default OrganisationGatewaysTable;
